import React, { useEffect, useState } from "react";
import Dropdown from "../../input/Dropdown";
import { getBulkDropdownData } from "../../../api/keyvalue";
import {
  getChargeTypesByHeaders,
  removeCharges,
} from "../../../api/financeAmendmentApi";
import Button from "../../input/Button";
import Input from "../../input/Input";
import { toast, ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import useUserCompany from "../../../hooks/useUserCompany";

const ChargeStatusChange = ({ closeModal, selectedRows, isOpen }) => {
  const [chargeCategory, setChargeCategory] = useState(null);
  const [chargeType, setChargeType] = useState(null);
  const [removalRemark, setRemovalRemark] = useState("");
  const [errors, setErrors] = useState({});

  const [chargeCategoryOptions, setChargeCategoryOptions] = useState([]);
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [isChargeTypeLoading, setIsChargeTypeLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = useSelector((state) => state.user);
  const company = useUserCompany();
  useEffect(() => {
    (async () => {
      const { revcost: revcostData } = await getBulkDropdownData([
        { key: "revcost" },
      ]);
      setChargeCategoryOptions(revcostData);
    })();
  }, []);

  useEffect(() => {
    if (isOpen && selectedRows && selectedRows.length > 0) {
      fetchChargeTypes();
    }
  }, [isOpen, selectedRows]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const fetchChargeTypes = async () => {
    try {

      setIsChargeTypeLoading(true);
      const headerIds = selectedRows.map((row) => row.chgId);
      const chargeTypes = await getChargeTypesByHeaders(headerIds);

      const mappedOptions = chargeTypes.map((ct) => ({
        key: ct.chtId,
        text: ct.chtCharge,
        value: ct.chtChargeValue,
      }));

      setChargeTypeOptions(mappedOptions);
      setIsChargeTypeLoading(false);
    } catch (error) {
      console.error("Error loading charge types:", error);
      setIsChargeTypeLoading(false);
      toast.error("Failed to load charge types", {
        autoClose: 2000,
        containerId: "Modal",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!chargeCategory) {
      newErrors.chargeCategory = "Charge category is required";
    }

    if (!chargeType) {
      newErrors.chargeType = "Charge type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const items = selectedRows.map((row) => ({
        jobId: row.jobId,
        requestId: row.requestId,
        chhId: row.chgId,
      }));

      const payload = {
        items: items,
        chargeCategory: chargeCategory,
        chargeType: chargeType,
        removalRemark: removalRemark || "",
      };

      const responses = await removeCharges(
        payload,
        user.username,
        company.comId,
      );

      const successCount = responses.filter((r) => r.success).length;
      const failureCount = responses.filter((r) => !r.success).length;

      responses.forEach((response) => {
        if (response.success) {
          toast.success(
            `Charge Header ${response.chhId}: ${response.message}`,
            {
              autoClose: 2000,
              containerId: "Modal",
            },
          );
        } else {
          toast.error(`Charge Header ${response.chhId}: ${response.message}`, {
            autoClose: 4000,
            containerId: "Modal",
          });
        }
      });

      if (successCount > 0 && failureCount > 0) {
        toast.info(
          `Completed: ${successCount} succeeded, ${failureCount} failed`,
          {
            autoClose: 3000,
            containerId: "Modal",
          },
        );
      } else if (successCount === responses.length) {
        toast.success(`All ${successCount} charges removed successfully!`, {
          autoClose: 2000,
          containerId: "Modal",
        });
      } else {
        toast.error(`All ${failureCount} operations failed`, {
          autoClose: 3000,
          containerId: "Modal",
        });
      }

      setIsSubmitting(false);

      if (successCount === responses.length) {
        setTimeout(() => {
          closeModal();
        }, 4000);
      }
    } catch (error) {
      console.error("Error removing charges:", error);
      setIsSubmitting(false);
      toast.error(
        error.response?.data?.message ||
          "Failed to remove charges. Please try again.",
        {
          autoClose: 3000,
          containerId: "Modal",
        },
      );
    }
  };

  const resetForm = () => {
    setChargeCategory(null);
    setChargeType(null);
    setRemovalRemark("");
    setErrors({});
    setChargeTypeOptions([]);
  };

  const resetError = (field) => {
    setErrors((prevState) => {
      const { [field]: _, ...rest } = prevState;
      return rest;
    });
  };

  return (
    <div>
      <div className="ui form">
        <div className="ui segment">
          <h4 className="ui dividing header">Charge Removal</h4>

          <div className="ui segment">
            <div className="two fields">
              <Dropdown
                required={true}
                label="Charge Category"
                placeholder="Select charge category"
                error={errors.chargeCategory}
                resetError={() => resetError("chargeCategory")}
                style={{ height: "20px" }}
                options={chargeCategoryOptions}
                value={chargeCategory}
                onChange={(e, data) => {
                  setChargeCategory(data.value);
                  resetError("chargeCategory");
                }}
              />

              <Dropdown
                required={true}
                label="Charge Type"
                placeholder="Select charge type"
                error={errors.chargeType}
                resetError={() => resetError("chargeType")}
                style={{ height: "20px" }}
                options={chargeTypeOptions}
                value={chargeType}
                loading={isChargeTypeLoading}
                disabled={isChargeTypeLoading || chargeTypeOptions.length === 0}
                onChange={(e, data) => {
                  setChargeType(data.value);
                  resetError("chargeType");
                }}
              />
            </div>

            <div className="field">
              <Input
                label="Remark"
                placeholder="Enter removal remark (optional)"
                value={removalRemark}
                onChange={(e) => setRemovalRemark(e.target.value)}
              />
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Button
                  label="Remove Charges"
                  onClick={handleSubmit}
                  className="ui negative button"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargeStatusChange;
